import {openUserDesignDownloadModal, downloadGeneratedImageAsset} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import {sendPrompt} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {ImageQualityOption} from '@Components/download-panel/components/ai-chat-image-download-panel-content/ai-chat-image-download-panel-content.types';
import type {ButtonAction} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import {ButtonActionType, DownloadFileType, DownloadImageQuality} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import type {AiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.types';
import {UsedAssetKind} from '@AiChat/callbacks/ai-chat-callbacks.types';
import {showPurchaseCreditsDialog} from '@Libraries/ai-chat.library';
import {openPricingWithPromo} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {NewSubscriptionStartSource} from '@Libraries/ga-events';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {assertNever} from '@Utils/assert-never.util';

export const dispatchButtonAction = async (action: ButtonAction, promptBoxId: string, callbacks: AiChatCallbacks): Promise<void> => {
  switch (action.type) {
    case ButtonActionType.USE_THIS: {
      callbacks.onUseAsset?.({kind: UsedAssetKind.IMAGE, chatId: action.chatId, messageId: null, imageHashedId: action.hashedId});
      return;
    }
    case ButtonActionType.DOWNLOAD: {
      await downloadGeneratedImageAsset(action.chatId, action.hashedId, toQualityOption(action.imageQuality), toFileTypeOption(action.fileType));
      return;
    }
    case ButtonActionType.DOWNLOAD_DESIGN: {
      openUserDesignDownloadModal(action.posterHashedId, action.pageHashedId);
      return;
    }
    case ButtonActionType.TURN_INTO_VIDEO: {
      // TurnIntoVideoButton owns the generation hook, so a video button never reaches this dispatcher.
      return;
    }
    case ButtonActionType.FILL_PROMPT: {
      sendPrompt(promptBoxId, action.text);
      return;
    }
    case ButtonActionType.UPSELL_PREMIUM: {
      openPricingWithPromo(NewSubscriptionStartSource.AI_CHAT_AGENT_UPSELL, action.promoCode);
      return;
    }
    case ButtonActionType.UPGRADE_PREMIUM: {
      openPricingWithPromo(NewSubscriptionStartSource.AI_CHAT_AGENT_UPSELL);
      return;
    }
    case ButtonActionType.UPSELL_CREDITS: {
      openCreditPackCheckout();
      return;
    }
    default:
      assertNever(action);
  }
};

/** Fire-and-forget so the button never waits on a checkout the user can close without buying. */
const openCreditPackCheckout = (): void => {
  void showPurchaseCreditsDialog().then((purchaseData) => {
    if (!purchaseData?.creditsV2) {
      return;
    }

    void refreshTeamCreditsInStore();
  });
};

const toFileTypeOption = (fileType: DownloadFileType): FileTypeOptions => {
  if (fileType === DownloadFileType.PDF) {
    return FileTypeOptions.PDF;
  }

  return FileTypeOptions.PNG;
};

const toQualityOption = (quality: DownloadImageQuality): ImageQualityOption => {
  if (quality === DownloadImageQuality.ENHANCED_4K) {
    return ImageQualityOption.ENHANCED_4K;
  }

  return ImageQualityOption.ORIGINAL;
};
